import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Popconfirm, Select, Table, Tag } from "antd";
import { TASK_PRIORITY_LABELS } from "@/modules/tasks/constants";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import {
	RECURRENCE_COLORS,
	RECURRENCE_LABELS,
	RECURRENCE_OPTIONS,
	WEEKDAY_LABELS,
} from "../constants";
import { useDeleteTaskSchedule, useTaskSchedules } from "../hooks/useTaskSchedules";
import { useTaskScheduleStore } from "../store/task-schedule.store";
import type { TaskRecurrence, TaskSchedule } from "../types";

function recurrenceDetail(record: TaskSchedule): string {
	const suffix = record.intervalCount > 1 ? ` (har ${record.intervalCount})` : "";
	switch (record.recurrence) {
		case "WEEKLY":
			return `${(record.weekdays ?? []).map((d) => WEEKDAY_LABELS[d] ?? d).join(", ") || "—"}${suffix}`;
		case "MONTHLY":
			return record.dayOfMonth != null ? `${record.dayOfMonth}-kuni${suffix}` : "—";
		default:
			return `Har kuni${suffix}`;
	}
}

export function TaskScheduleTable() {
	const { onOpen } = useTaskScheduleStore();
	const { page: currentPage, limit: pageSize, setPage, getFilter, setFilters } = useUrlTableState();
	const recurrence = getFilter("recurrence") as TaskRecurrence | undefined;
	const { data, isLoading } = useTaskSchedules({
		page: currentPage,
		limit: pageSize,
		recurrence,
	});
	const { mutate: remove } = useDeleteTaskSchedule();
	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;

	return (
		<div>
			<TableToolbar
				filters={
					<Select
						placeholder="Takrorlanish bo'yicha filtr"
						allowClear
						value={recurrence}
						onChange={(value) => setFilters({ recurrence: value ?? null })}
						options={RECURRENCE_OPTIONS}
						style={{ width: 220 }}
					/>
				}
			/>
			<Table
				loading={isLoading}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				locale={{
					emptyText: <EmptyState description="Takrorlanish jadvali topilmadi" />,
				}}
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{
						key: "title",
						title: "Sarlavha",
						dataIndex: "title",
					},
					{
						key: "taskType",
						title: "Turi",
						render: (_: unknown, record: TaskSchedule) =>
							record.taskType?.name ?? record.taskTypeId,
					},
					{
						key: "brigade",
						title: "Brigada",
						render: (_: unknown, record: TaskSchedule) => record.brigade?.name ?? "—",
					},
					{
						key: "recurrence",
						title: "Takrorlanish",
						width: 130,
						render: (_: unknown, record: TaskSchedule) => (
							<Tag color={RECURRENCE_COLORS[record.recurrence] ?? "default"}>
								{RECURRENCE_LABELS[record.recurrence] ?? record.recurrence}
							</Tag>
						),
					},
					{
						key: "detail",
						title: "Qachon",
						width: 180,
						render: (_: unknown, record: TaskSchedule) => recurrenceDetail(record),
					},
					{
						key: "timeOfDay",
						title: "Vaqti",
						width: 90,
						dataIndex: "timeOfDay",
					},
					{
						key: "priority",
						title: "Ustuvorlik",
						width: 110,
						render: (_: unknown, record: TaskSchedule) =>
							TASK_PRIORITY_LABELS[record.priority] ?? record.priority,
					},
					{
						key: "isActive",
						title: "Holat",
						dataIndex: "isActive",
						width: 80,
						render: (isActive: boolean) => (
							<StatusTag tone={isActive ? "success" : "default"}>
								{isActive ? "Faol" : "Nofaol"}
							</StatusTag>
						),
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_: unknown, record: TaskSchedule) => (
							<Dropdown
								menu={{
									items: [
										{
											key: "edit",
											icon: <EditOutlined />,
											label: "Tahrirlash",
											onClick: () => onOpen(record),
										},
										{
											key: "delete",
											danger: true,
											label: (
												// biome-ignore lint/a11y/useKeyWithClickEvents: dropdown menu item
												// biome-ignore lint/a11y/noStaticElementInteractions: dropdown menu item
												<div onClick={(e) => e.stopPropagation()}>
													<Popconfirm
														title="O'chirish"
														description="Rostdan ham o'chirmoqchimisiz?"
														onConfirm={() => remove(record.id)}
														okText="Ha"
														cancelText="Yo'q"
														okButtonProps={{ danger: true }}
													>
														<div
															style={{
																width: "100%",
																display: "flex",
																alignItems: "center",
																gap: "8px",
															}}
														>
															<DeleteOutlined />
															<span>O&apos;chirish</span>
														</div>
													</Popconfirm>
												</div>
											),
										},
									],
								}}
								trigger={["click"]}
								placement="bottomRight"
							>
								<Button type="text" icon={<MoreOutlined />} size="small" />
							</Dropdown>
						),
					},
				]}
			/>
		</div>
	);
}
